import { useCallback, useMemo, useRef } from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import AddBoxIcon from '@mui/icons-material/AddBox'
import AppsIcon from '@mui/icons-material/Apps'
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox'
import { ButtonGroup, Stack } from '@mui/material'
import PropTypes from 'prop-types'
import {
  ActionButton,
  Editable,
  PictoGroup,
  ToggleableButton,
} from '~/components'
import { DnDTypes } from '~/constants'
import { useCanEdit } from '~/hooks'
import { useRouteContext } from '~/routes'
import { useCategoryDetailsContext, useGroupContext } from '../hooks'

const Property = (props) => {
  const { index, property } = props
  const { t } = useTranslation()
  const canEdit = useCanEdit()
  const navigate = useNavigate()

  const { product } = useRouteContext(Property.name)

  const {
    saveProperty,
    onAttachProperty,
    onDetachProperty,
    onPropertyMove,
    onPropertyDrop,
    onPropertyToggleSkip,
  } = useCategoryDetailsContext(Property.name)

  const groupContext = useGroupContext(Property.name, false)

  const ref = useRef()

  const [{ isDragging }, drag] = useDrag({
    type: DnDTypes.PROPERTY,
    item: () => {
      return { id: property.id, index }
    },
    canDrag: () => canEdit,
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })
  const [{ canDrop, handlerId }, drop] = useDrop({
    accept: DnDTypes.PROPERTY,
    collect(monitor) {
      return {
        canDrop: monitor.canDrop(),
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item, monitor) {
      if (!ref.current) {
        return
      }
      // console.log('item', monitor.getItemType())
      const dragIndex = item.index
      const hoverIndex = index
      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return
      }
      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect()
      // Determine mouse position
      const clientOffset = monitor.getClientOffset()
      // Get pixels to the top
      const hoverClientY = clientOffset.y - hoverBoundingRect.top
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%
      if (
        // Dragging downwards
        (dragIndex < hoverIndex &&
          hoverClientY < hoverBoundingRect.height * 0.1) ||
        // Dragging upwards
        (dragIndex > hoverIndex &&
          hoverClientY > hoverBoundingRect.height * 0.9)
      ) {
        return
      }
      // Time to actually perform the action
      onPropertyMove(item.id, property.id)
      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex
    },
    drop() {
      onPropertyDrop()
    },
  })

  const setNodeRef = useCallback(
    (node) => {
      ref.current = node
      drag(drop(node))
    },
    [drag, drop]
  )
  const opacity = isDragging ? 0.5 : 1

  const status = useMemo(
    () => property.status.replace(/V/, ''),
    [property.status]
  )

  const hasValueStatus = useMemo(
    () => status !== property.status,
    [property.status, status]
  )

  const onPictoClick = useCallback(
    (letter) => {
      switch (letter) {
        case 'Z':
          saveProperty({
            model: property.model,
            id: property.id,
            description: null,
          })
          break
        case 'M':
          saveProperty({
            model: property.model,
            id: property.id,
            value: null,
          })
          break
      }
    },
    [property, saveProperty]
  )

  return (
    <Stack
      ref={setNodeRef}
      data-handler-id={handlerId}
      sx={{ opacity }}
      direction="row"
      spacing={1}
    >
      <Stack
        direction="row"
        sx={{ flexGrow: 1, gap: '8px', minWidth: '1px' }}
        useFlexGap
      >
        <Editable
          sx={{ width: 'calc(50% - 4px)' }}
          value={property.description}
          variant="name"
          onStore={(description) => {
            saveProperty({
              model: property.model,
              id: property.id,
              description,
            })
            return true
          }}
        >
          {!canDrop && status && canEdit && (
            <PictoGroup
              disabledLetters="M"
              text={status}
              onPictoClick={onPictoClick}
            />
          )}
        </Editable>
        <Editable
          disabled={!product}
          sx={{ width: 'calc(50% - 4px)' }}
          value={property.value}
          variant="value"
          onStore={(value) => {
            saveProperty({
              model: property.model,
              id: property.id,
              value,
            })
            return true
          }}
        >
          {!canDrop && hasValueStatus && canEdit && (
            <PictoGroup text="M" onPictoClick={onPictoClick} />
          )}
        </Editable>
      </Stack>
      {canDrop ? (
        <div style={{ width: '96px' }} />
      ) : (
        <ButtonGroup
          sx={{
            alignItems: 'center',
          }}
        >
          {groupContext ? (
            <>
              {canEdit && (
                <ActionButton
                  icon={<IndeterminateCheckBoxIcon />}
                  title={t('property.detach')}
                  onClick={() => onDetachProperty(property)}
                />
              )}
              {canEdit && (
                <ToggleableButton
                  label="N"
                  on={!!property.skip}
                  title={t('property.skip')}
                  onChange={() =>
                    onPropertyToggleSkip({
                      property,
                      group: groupContext.group,
                      letter: 'N',
                    })
                  }
                />
              )}
              {canEdit && (
                <ToggleableButton
                  label="O"
                  on={!!property.skip_komputronik}
                  title={t('property.skip-komputronik')}
                  onChange={() =>
                    onPropertyToggleSkip({
                      property,
                      group: groupContext.group,
                      letter: 'O',
                    })
                  }
                />
              )}
              <ActionButton
                icon={<AppsIcon />}
                title={t('property.matrix')}
                onClick={() => {
                  navigate({
                    pathname: location.pathname,
                    hash: '#matrix/' + property.id,
                  })
                }}
              />
            </>
          ) : (
            canEdit && (
              <ActionButton
                icon={<AddBoxIcon />}
                onClick={() => onAttachProperty(property)}
              />
            )
          )}
        </ButtonGroup>
      )}
    </Stack>
  )
}

Property.propTypes = {
  index: PropTypes.number.isRequired,
  property: PropTypes.shape({
    description: PropTypes.string.isRequired,
    id: PropTypes.number.isRequired,
    model: PropTypes.string.isRequired,
    skip: PropTypes.number,
    skip_komputronik: PropTypes.number,
    status: PropTypes.string.isRequired,
    value: PropTypes.string,
  }).isRequired,
}

export default Property
